import React, { useState } from "react";
import { useTranslation } from "context/TranslationProvider";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Alert,
  Spinner
} from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";

function EvidenceModal({ isOpen, toggle, fileUrl, uploadedAt }) {
  const { t } = useTranslation();

  // Interactive controls state
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  if (!isOpen) return null;

  // Determine file metadata
  const cleanUrl = fileUrl ? fileUrl.split("?")[0] : "";
  const filename = cleanUrl ? cleanUrl.split("/").pop() : "evidence-file";
  const fileExtension = cleanUrl ? cleanUrl.split(".").pop().toLowerCase() : "";

  const isImage = ["jpg", "jpeg", "png", "webp", "gif"].includes(fileExtension);
  const isPdf = fileExtension === "pdf";

  // Formatted upload date
  const formattedDate = uploadedAt
    ? new Date(uploadedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }) +
      " • " +
      new Date(uploadedAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }) + " • 02:15 PM"; // Default matched to design mockup fallback

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 10, 300));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 10, 50));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleReset = () => {
    setZoom(100);
    setRotation(0);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="modal-dialog-centered" style={{ maxWidth: "1000px" }}>
      {/* ─── CUSTOM HEADER ─── */}
      <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom bg-white" style={{ borderRadius: "8px 8px 0 0" }}>
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#EEF2FF",
              color: "#4F46E5",
              flexShrink: 0
            }}
          >
            <InsertDriveFileIcon style={{ fontSize: "20px" }} />
          </div>
          <div className="ml-2">
            <h4 className="mb-0 font-weight-bold text-slate-800" style={{ fontSize: "16px", lineHeight: "1" }}>
              {t("categories.moderation.evidence.title", "Evidence File Review")}
            </h4>
            <small className="text-muted text-xs">
              {t("categories.moderation.evidence.subtitle", "Review the submitted evidence file")}
            </small>
          </div>
        </div>
        <button
          onClick={toggle}
          className="border-0 bg-transparent text-muted hover:text-slate-800 transition-colors p-1"
          style={{ cursor: "pointer" }}
        >
          <CloseIcon style={{ fontSize: "20px" }} />
        </button>
      </div>

      <ModalBody className="p-4" style={{ backgroundColor: "#FAFBFC" }}>
        {!fileUrl ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Row>
            {/* Left Panel: Preview (Col-8) */}
            <Col md="8" xs="12" className="d-flex flex-column gap-3 mb-3 mb-md-0">

              {/* Rich Preview Frame */}
              <div
                className="position-relative rounded overflow-hidden d-flex flex-column align-items-center justify-content-center"
                style={{
                  height: "420px",
                  backgroundColor: "#111827",
                  border: "1px solid #E5E7EB"
                }}
              >
                {/* Images */}
                {isImage && (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden"
                    }}
                  >
                    <img
                      src={fileUrl}
                      alt="Evidence preview"
                      style={{
                        maxWidth: "90%",
                        maxHeight: "90%",
                        objectFit: "contain",
                        transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                        transition: "transform 0.15s ease-out",
                        userSelect: "none"
                      }}
                    />
                  </div>
                )}

                {/* PDFs */}
                {isPdf && (
                  <iframe
                    src={fileUrl}
                    title="PDF Evidence Preview"
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                )}

                {/* Other Unsupported Types */}
                {!isImage && !isPdf && (
                  <div className="p-4 w-100">
                    <Alert color="warning" className="text-left mb-0">
                      <h4 className="alert-heading">{t("categories.moderation.evidence.previewUnavailable", "Preview Unavailable")}</h4>
                      <p className="mb-0 text-sm">
                        {t("categories.moderation.evidence.unsupportedDesc", "This file type is unsupported for in-browser rendering. Use the download button above to inspect locally.")}
                      </p>
                    </Alert>
                  </div>
                )}

                {/* Interactive Controls Overlay for Images */}
                {isImage && (
                  <div
                    className="position-absolute d-flex align-items-center justify-content-between px-3 py-2"
                    style={{
                      bottom: "12px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(31, 41, 55, 0.8)",
                      backdropFilter: "blur(4px)",
                      borderRadius: "8px",
                      gap: "24px",
                      zIndex: 10
                    }}
                  >
                    {/* Zoom panel */}
                    <div className="d-flex align-items-center gap-2 text-white text-xs font-weight-bold">
                      <button
                        onClick={handleZoomOut}
                        className="bg-transparent border-0 text-white hover:text-slate-300 p-0 d-flex"
                      >
                        <ZoomOutIcon style={{ fontSize: "16px" }} />
                      </button>
                      <span style={{ minWidth: "35px", textAlign: "center" }}>{zoom}%</span>
                      <button
                        onClick={handleZoomIn}
                        className="bg-transparent border-0 text-white hover:text-slate-300 p-0 d-flex"
                      >
                        <ZoomInIcon style={{ fontSize: "16px" }} />
                      </button>
                    </div>

                    <div style={{ width: "1px", height: "12px", backgroundColor: "rgba(255,255,255,0.2)" }} />

                    {/* Rotate, Fit panels */}
                    <div className="d-flex align-items-center gap-3">
                      <button
                        onClick={handleReset}
                        title={t("categories.moderation.evidence.reset", "Fit screen / Reset")}
                        className="bg-transparent border-0 text-white hover:text-slate-300 p-0 d-flex"
                      >
                        <AspectRatioIcon style={{ fontSize: "16px" }} />
                      </button>
                      <button
                        onClick={handleRotate}
                        title={t("categories.moderation.evidence.rotate", "Rotate 90°")}
                        className="bg-transparent border-0 text-white hover:text-slate-300 p-0 d-flex"
                      >
                        <RotateRightIcon style={{ fontSize: "16px" }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Col>

            {/* Right Panel: File Information (Col-4) */}
            <Col md="4" xs="12">
              <div
                className="p-4 h-100 rounded border bg-white"
                style={{ minHeight: "200px" }}
              >
                <h5 className="font-weight-bold text-slate-800 mb-3 d-flex align-items-center" style={{ fontSize: "14px" }}>
                  <InfoOutlinedIcon className="text-primary mr-2" style={{ fontSize: "18px" }} />
                  {t("categories.moderation.evidence.fileInfo", "File Information")}
                </h5>

                <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #EDF2F7" }}>
                  <span className="text-muted text-[10px] d-block text-uppercase font-weight-600 tracking-wider mb-1">
                    {t("categories.moderation.evidence.fileName", "File Name")}
                  </span>
                  <span className="font-weight-bold text-slate-700 text-sm text-break">
                    {filename}
                  </span>
                </div>

                <div className="mb-3 pb-3" style={{ borderBottom: "1px solid #EDF2F7" }}>
                  <span className="text-muted text-[10px] d-block text-uppercase font-weight-600 tracking-wider mb-1">
                    {t("categories.moderation.evidence.uploadedOn", "Uploaded On")}
                  </span>
                  <span className="font-weight-bold text-slate-700 text-sm">
                    {formattedDate}
                  </span>
                </div>

                <div>
                  <Button
                    outline
                    size="sm"
                    color="primary"
                    href={fileUrl}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="font-weight-600 d-flex align-items-center justify-content-center px-3"
                    style={{ borderRadius: "6px", fontSize: "12px" }}
                  >
                    <DownloadIcon style={{ fontSize: "14px" }} className="mr-1" />
                    {t("categories.moderation.evidence.download", "Download")} {fileExtension.toUpperCase()}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </ModalBody>

      <ModalFooter className="border-top bg-white py-3">
        <Button
          color="secondary"
          onClick={toggle}
          className="font-weight-600 px-4"
          style={{ borderRadius: "6px", fontSize: "12px" }}
        >
          {t("common.close", "Close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default EvidenceModal;
